/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// ── Final Sale Badge Swap ────────────────────────────────────────────────────
// When data-initial-price on a product-item ends in 97 cents, swap the SALE
// badge to "Final Sale". Also responds to variant:changed for swatch clicks.
(function () {
  function isFinalSalePrice(priceInCents) {
    return priceInCents % 100 === 97;
  }

  function updateFinalSaleBadge(productItem, priceInCents) {
    var badge = productItem.querySelector('.label--highlight');
    if (!badge) return;
    if (isFinalSalePrice(priceInCents)) {
      badge.textContent = 'Final Sale';
      badge.classList.add('label--final-sale');
    } else {
      badge.textContent = 'SALE';
      badge.classList.remove('label--final-sale');
    }
  }

  // Product-page badge is in .product-meta__label-list (different from collection cards)
  function updateProductPageBadge(priceInCents) {
    var badge = document.querySelector('.product-meta__label-list .label--highlight');
    if (!badge) return;
    if (isFinalSalePrice(priceInCents)) {
      badge.textContent = 'Final Sale';
      badge.classList.add('label--final-sale');
    } else {
      badge.textContent = 'SALE';
      badge.classList.remove('label--final-sale');
    }
  }

  function updateProductFinalSaleDisclaimer(priceInCents) {
    var disclaimer = document.querySelector('product-tooltip[data-final-sale-potential="true"]');
    if (!disclaimer) return;

    if (isFinalSalePrice(priceInCents)) {
      disclaimer.classList.remove('is-hidden');
    } else {
      disclaimer.classList.add('is-hidden');
    }
  }

  function initProductItems() {
    // Collection page cards
    document.querySelectorAll('product-item[data-initial-price]').forEach(function (productItem) {
      var price = parseInt(productItem.dataset.initialPrice, 10);
      if (!isNaN(price)) {
        updateFinalSaleBadge(productItem, price);
      }
    });
    // Product page initial state (badges and disclaimer)
    var productSection = document.querySelector('[data-product-initial-price]');
    if (productSection) {
      var price = parseInt(productSection.dataset.productInitialPrice, 10);
      if (!isNaN(price)) {
        updateProductPageBadge(price);
        updateProductFinalSaleDisclaimer(price);
      }
    }
  }

  // custom.js is deferred — DOM is ready by the time this runs
  initProductItems();

  // Re-run after AJAX pagination / facet filter updates
  document.addEventListener('page:loaded', initProductItems);

  // Focal theme fires variant:changed when a swatch is selected (product page / quick buy)
  // event.detail.variant.price is in cents
  document.addEventListener('variant:changed', function (e) {
    var variant = e.detail && e.detail.variant;
    if (!variant) return;

    // Collection page: product card
    var productItem = e.target.closest('product-item');
    if (productItem) {
      updateFinalSaleBadge(productItem, variant.price);
      return;
    }

    // Product page: handles both the badge and the disclaimer
    // We be permissive with the target as it often comes from the form
    var isProductPageTrigger = e.target.tagName === 'VARIANT-PICKER' ||
      e.target.closest('variant-picker') ||
      e.target.tagName === 'FORM' ||
      e.target.getAttribute('is') === 'product-form';

    if (isProductPageTrigger) {
      var price = variant.price;
      updateProductFinalSaleDisclaimer(price);
      // Wait a tick for the badge as it might be part of a rerender
      setTimeout(function () { updateProductPageBadge(price); }, 50);
    }
  });

  // Collection page swatches are plain radio inputs — they fire `change`, not `variant:changed`
  // The data-variant-price attribute on each radio carries the price in cents
  document.addEventListener('change', function (e) {
    if (e.target && e.target.classList.contains('color-swatch__radio')) {
      var productItem = e.target.closest('product-item');
      if (productItem && e.target.dataset.variantPrice) {
        updateFinalSaleBadge(productItem, parseInt(e.target.dataset.variantPrice, 10));
      }
    }
  });
}());